<template>
  <div class="screening-action-controls">
    <div class="labels-and-back-link mr-4">
      <div class="labels">
        <last-voters-cluster-prefix
          v-if="showLastVotersPrefix"
          :voters="lastVoters"
        />
        {{ label }}
      </div>
      <back-link
        v-if="showBackLink"
        current-stage="Full Text Review"
        @click="$emit('back')"
      />
    </div>
    <div class="actions">
      <vote-controls
        v-if="showVoteButtons"
        :disabled="disabled"
        @vote="onVoteButtonClick"
      />
      <assessment-dropdown
        v-if="showVoteDropdown"
        :value="dropdownValue"
        :hide-options="['UNSCREENED']"
        :show-revoke-option="allowRevoke"
        :disabled="disabled"
        @input="onVoteDropdownChange"
        @revoke="onRevokeVote"
      />
    </div>
  </div>
</template>

<script>
import VoteControls from '@/components/ScreenReferencesPage/VoteControls';
import LastVotersClusterPrefix from './LastVotersClusterPrefix';
import BackLink from './BackLink';
import AssessmentDropdown from '@/components/References/AssessmentDropdown';
import {
  Reference,
  Researcher,
  ReviewerScreeningVote,
  ScreeningOutcome,
  Review,
} from '@/models';
import {
  ReferenceActionState,
  RelevanceAssessment,
  ScreeningOutcomeResolution,
} from '@/enums';

const ACTION_STATE_TO_PROPS = {
  [ReferenceActionState.PENDING_ACTION]: {
    label: 'How would you like to screen this reference?',
    showVoteButtons: true,
  },
  [ReferenceActionState.PENDING_ACTION_BY_YOU]: {
    prefixWithLastVoter: true,
    label:
      'has voted on this reference. How would you like to screen this reference?',
    showVoteButtons: true,
  },
  [ReferenceActionState.PENDING_ACTION_ALREADY_TAKEN]: {
    label:
      'You have already voted on this reference. You may change it, otherwise no further action is required.',
    showVoteDropdown: true,
    dropdownValue: (reference, researcher) =>
      reference.reviewerScreeningVotes.find(
        (vote) => vote.reviewer?.researcher?.id === researcher.id
      ).vote,
  },
  [ReferenceActionState.PENDING_ACTION_BY_OTHERS]: {
    label:
      'Requires a vote from users with elevated permissions. No further action is required from you.',
  },
  [ReferenceActionState.PENDING_CONFLICT_RESOLUTION_BY_YOU]: {
    prefixWithLastVoters: true,
    label:
      'have conflicting votes. How would you like to resolve this?',
    showVoteButtons: true,
  },
  [ReferenceActionState.PENDING_CONFLICT_RESOLUTION_BY_OTHERS]: {
    prefixWithLastVoters: true,
    label:
      'have conflicting votes. No further action is required from you.',
  },
  [ReferenceActionState.EXCLUDED_BY_CONSENSUS]: {
    prefixWithLastVoters: true,
    label:
      'excluded this from the review after reaching consensus during Screening.',
    showVoteDropdown: true,
    dropdownValue: () => RelevanceAssessment.NOT_RELEVANT,
  },
  [ReferenceActionState.EXCLUDED_BY_CONFLICT_RESOLUTION]: {
    prefixWithLastVoter: true,
    label:
      'excluded this from the review after resolving a conflict during Screening.',
    showVoteDropdown: true,
    dropdownValue: () => RelevanceAssessment.NOT_RELEVANT,
  },
  [ReferenceActionState.EXCLUDED_BY_OVERRIDE]: {
    prefixWithLastVoter: true,
    label:
      'overrode the previous outcome and excluded this from the review.',
    showVoteDropdown: true,
    dropdownValue: () => RelevanceAssessment.NOT_RELEVANT,
  },
  [ReferenceActionState.EXCLUDED_ON_UPLOAD]: {
    prefixWithLastVoter: true,
    label: 'excluded this from the review during the upload process.',
    showVoteDropdown: true,
    dropdownValue: () => RelevanceAssessment.NOT_RELEVANT,
  },
  [ReferenceActionState.EXCLUDED_BY_SUGGESTION]: {
    prefixWithLastVoter: true,
    label:
      'excluded this from the review after the stopping criteria had been activated.',
    showVoteDropdown: true,
    dropdownValue: () => RelevanceAssessment.NOT_RELEVANT,
  },
  [ReferenceActionState.INCLUDED_BY_CONSENSUS]: {
    prefixWithLastVoters: true,
    label:
      'included this in the review after reaching consensus during Screening.',
    showVoteDropdown: true,
    showBackLink: true,
    dropdownValue: (reference) => reference.screeningOutcome.outcome,
  },
  [ReferenceActionState.INCLUDED_BY_CONFLICT_RESOLUTION]: {
    prefixWithLastVoter: true,
    label:
      'included this in the review after resolving a conflict during Screening.',
    showVoteDropdown: true,
    dropdownValue: (reference) => reference.screeningOutcome.outcome,
    showBackLink: true,
  },
  [ReferenceActionState.INCLUDED_BY_OVERRIDE]: {
    prefixWithLastVoter: true,
    label:
      'overrode the previous outcome and included this in the review.',
    showVoteDropdown: true,
    dropdownValue: (reference) => reference.screeningOutcome.outcome,
    showBackLink: true,
  },
  [ReferenceActionState.INCLUDED_ON_UPLOAD]: {
    prefixWithLastVoter: true,
    label: 'included this in the review during the upload process.',
    showVoteDropdown: true,
    dropdownValue: (reference) => reference.screeningOutcome.outcome,
    showBackLink: true,
  },
};

const VOTE_CONTROLS_TO_RELEVANCE_ASSESSMENT = {
  YES: RelevanceAssessment.RELEVANT,
  NO: RelevanceAssessment.NOT_RELEVANT,
  NOT_SURE: RelevanceAssessment.NOT_SURE,
};

export default {
  name: 'ScreeningActionControls',
  components: {
    VoteControls,
    AssessmentDropdown,
    LastVotersClusterPrefix,
    BackLink,
  },
  props: {
    reference: {
      type: Reference,
      required: true,
    },
    review: {
      type: Review,
      required: true,
    },
    currentResearcher: {
      type: Researcher,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'back',
    'create-screening-vote',
    'change-screening-vote',
    'revoke-screening-vote',
    'resolve-screening-conflict',
    'change-screening-outcome',
  ],
  computed: {
    prefixWithLastVoter() {
      return this.actionProps.prefixWithLastVoter;
    },
    prefixWithLastVoters() {
      return this.actionProps.prefixWithLastVoters;
    },
    showLastVotersPrefix() {
      return this.prefixWithLastVoter || this.prefixWithLastVoters;
    },
    lastVoters() {
      if (this.prefixWithLastVoter) {
        return [this.lastVoter];
      }
      return this.reference.reviewerScreeningVotes
        .slice(0, 2)
        .map((vote) => vote.reviewer?.researcher)
        .filter(Boolean);
    },
    lastVoter() {
      const useVotesToFindLastVoter = [
        ReferenceActionState.PENDING_ACTION_BY_YOU,
      ].includes(this.actionState);
      return useVotesToFindLastVoter
        ? this.reference.reviewerScreeningVotes[0]?.reviewer
            ?.researcher
        : this.reference.screeningOutcome?.resolvedBy?.researcher;
    },
    label() {
      return this.actionProps.label;
    },
    showVoteButtons() {
      return this.actionProps.showVoteButtons;
    },
    showVoteDropdown() {
      return this.actionProps.showVoteDropdown;
    },
    dropdownValue() {
      return this.actionProps.dropdownValue?.(
        this.reference,
        this.currentResearcher
      );
    },
    showBackLink() {
      return this.actionProps.showBackLink;
    },
    actionProps() {
      return ACTION_STATE_TO_PROPS[this.actionState];
    },
    actionState() {
      return this.reference.getScreeningActionState(
        this.review,
        this.currentResearcher
      );
    },
    allowRevoke() {
      return (
        this.actionState ===
        ReferenceActionState.PENDING_ACTION_ALREADY_TAKEN
      );
    },
    currentReviewMember() {
      return this.review.reviewMembers.find((member) => {
        return member.researcher.id === this.currentResearcher.id;
      });
    },
  },
  methods: {
    onVoteButtonClick(vote) {
      vote = VOTE_CONTROLS_TO_RELEVANCE_ASSESSMENT[vote];
      this.processAssessment(vote);
    },
    onVoteDropdownChange(outcome) {
      this.processAssessment(outcome);
    },
    processAssessment(assessment) {
      switch (this.actionState) {
        case ReferenceActionState.PENDING_ACTION:
        case ReferenceActionState.PENDING_ACTION_BY_YOU:
          this.onCreateScreeningVote(assessment);
          break;
        case ReferenceActionState.PENDING_ACTION_BY_OTHERS:
          this.onChangeScreeningVote(assessment);
          break;
        case ReferenceActionState.PENDING_CONFLICT_RESOLUTION_BY_YOU:
          this.onResolveConflict(assessment);
          break;
        default:
          this.onChangeScreeningOutcome(assessment);
          break;
      }
    },
    onCreateScreeningVote(vote) {
      const voteToCreate = new ReviewerScreeningVote({
        vote,
        reviewer: this.currentReviewMember,
        reference: this.reference,
      });
      this.$emit('create-screening-vote', voteToCreate);
    },
    onChangeScreeningVote(vote) {
      const existingVote = this.reference.getScreeningVoteBy(
        this.currentResearcher
      );
      if (!existingVote) {
        throw new Error(
          `Unable to find an existing vote for ${this.currentResearcher}`
        );
      }
      const voteToChange = new ReviewerScreeningVote({
        id: existingVote.id,
        vote,
        reviewer: this.currentReviewMember,
        reference: this.reference,
      });
      this.$emit('change-screening-vote', voteToChange);
    },
    onRevokeVote() {
      const existingVote = this.reference.getScreeningVoteBy(
        this.currentResearcher
      );
      if (!existingVote) {
        throw new Error(
          `Unable to find an existing vote for ${this.currentResearcher}`
        );
      }
      this.$emit('revoke-screening-vote', existingVote);
    },
    onResolveConflict(outcome) {
      this.$emit('resolve-screening-conflict', {
        reference: this.reference,
        outcome,
      });
    },
    onChangeScreeningOutcome(outcome) {
      const existingOutcome = this.reference.screeningOutcome;
      if (!existingOutcome) {
        throw new Error(
          `Unable to find an existing screening outcome for ${this.reference}`
        );
      }
      const outcomeToChange = new ScreeningOutcome({
        id: existingOutcome.id,
        outcome,
        reference: this.reference,
        resolution: ScreeningOutcomeResolution.UPDATED_OUTCOME,
      });
      this.$emit('change-screening-outcome', outcomeToChange);
    },
  },
};
</script>

<style lang="scss" scoped>
.screening-action-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;

  font-size: 14px;
  font-weight: 300;

  .inline-block {
    display: inline-block;
  }
}
</style>
